// Copied from platform-app utils/iconRegistry.ts on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

function isIcon(val: any): val is LucideIcon {
  return val && (typeof val === 'function' || (typeof val === 'object' && val.$$typeof));
}

export function getIcon(name: string): LucideIcon {
  if (!name) return LucideIcons.Package;
  const icon = (LucideIcons as any)[name];
  if (isIcon(icon)) return icon;
  const pascalCase = name
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
  const iconPascal = (LucideIcons as any)[pascalCase];
  if (isIcon(iconPascal)) return iconPascal;
  return LucideIcons.Package;
}

// Curated icons organized by category — 700+ icons for POS, Cafe, Real Estate, and general app use
export const CURATED_ICONS: Record<string, string[]> = {
  'POS & Retail': [
    'ShoppingCart', 'ShoppingBag', 'Store', 'Tag', 'Tags', 'Receipt', 'CreditCard',
    'Wallet', 'DollarSign', 'Banknote', 'Coins', 'BadgeDollarSign', 'BadgePercent',
    'Percent', 'BarChart3', 'BarChart', 'TrendingUp', 'PieChart', 'LineChart',
    'Truck', 'Package', 'Boxes', 'ScanBarcode', 'QrCode', 'Printer',
    'Calculator', 'Scale', 'Weight', 'Warehouse', 'CircleDollarSign',
    'HandCoins', 'ReceiptText', 'Scan', 'TicketCheck',
    'PackageCheck', 'PackageOpen', 'PackagePlus', 'PackageSearch', 'PackageX',
    'PackageMinus', 'Gift', 'Ticket', 'TicketPercent',
    'BaggageClaim', 'Container', 'Barcode',
  ],
  'Cafe & Food': [
    'Coffee', 'CupSoda', 'UtensilsCrossed', 'ChefHat', 'Soup', 'Salad',
    'Sandwich', 'Pizza', 'Cake', 'IceCream', 'Cookie', 'Croissant',
    'Wine', 'Beer', 'GlassWater', 'Milk', 'Apple', 'Cherry', 'Grape',
    'Wheat', 'Leaf', 'Vegan', 'Flame', 'Thermometer',
    'BookOpen', 'ClipboardList', 'ScrollText',
    'Citrus', 'Egg', 'Ham', 'Popcorn', 'Drumstick', 'Fish',
    'Candy', 'Dessert', 'Martini', 'CookingPot',
    'Banana', 'Nut', 'Bean', 'Carrot', 'Mushroom',
    'Beef', 'Microwave', 'Refrigerator',
  ],
  'Real Estate & Property': [
    'Home', 'Building', 'Building2', 'Hotel', 'Landmark', 'Castle',
    'DoorOpen', 'DoorClosed', 'Key', 'KeyRound', 'Lock', 'Unlock',
    'Map', 'MapPin', 'MapPinned', 'Navigation', 'Compass',
    'Ruler', 'PencilRuler', 'HardHat', 'Hammer', 'Wrench', 'PaintBucket',
    'Bed', 'Bath', 'Sofa', 'Lamp', 'Fan', 'AirVent',
    'Trees', 'Fence', 'ParkingSquare', 'Car',
    'House', 'HousePlus', 'Warehouse', 'BrickWall', 'Paintbrush',
    'Construction', 'Drill', 'Heater',
    'LockKeyhole', 'LockOpen', 'KeySquare',
    'Armchair', 'LampDesk', 'LampFloor', 'LampCeiling',
    'Blinds', 'Pipette', 'TreeDeciduous', 'TreePine', 'Flower', 'Flower2',
    'Sprout', 'Shrub', 'Mountain', 'MountainSnow',
  ],
  'People & Team': [
    'Users', 'User', 'UserPlus', 'UserCheck', 'UserCog', 'UserCircle',
    'Contact', 'BadgeCheck', 'Shield', 'ShieldCheck', 'ShieldAlert',
    'Handshake', 'HeartHandshake', 'Award', 'Trophy', 'Medal', 'Crown',
    'UserMinus', 'UserX', 'UsersRound', 'UserRound', 'UserRoundCheck',
    'CircleUserRound', 'PersonStanding', 'Baby', 'Accessibility',
    'GraduationCap', 'Briefcase', 'IdCard',
    'UserRoundPlus', 'UserRoundMinus', 'UserRoundX', 'UserRoundCog',
    'Contact2', 'ContactRound', 'UserPen',
    'UserSearch', 'Group', 'Network',
  ],
  'Schedule & Tasks': [
    'Calendar', 'CalendarCheck', 'CalendarDays', 'CalendarClock', 'CalendarPlus',
    'Clock', 'Timer', 'TimerReset', 'Alarm', 'Hourglass',
    'ListTodo', 'ListChecks', 'CheckSquare', 'CircleCheck', 'ClipboardCheck',
    'Target', 'Flag', 'Bookmark', 'Star',
    'CalendarRange', 'CalendarSearch', 'AlarmClock',
    'CircleDot', 'ListOrdered', 'ListFilter', 'Kanban',
    'Milestone', 'Route', 'Workflow',
    'CalendarX', 'CalendarMinus', 'CalendarOff', 'CalendarHeart',
    'Clock2', 'Clock3', 'Clock4', 'Clock8', 'Clock10',
    'TimerOff', 'AlarmCheck', 'AlarmMinus', 'AlarmPlus',
    'BookmarkCheck', 'BookmarkMinus', 'BookmarkPlus', 'BookmarkX',
    'FlagOff', 'FlagTriangleRight',
    'SquareCheck', 'SquareCheckBig', 'CircleCheckBig',
    'ListEnd', 'ListStart', 'ListRestart', 'ListTree', 'ListVideo', 'ListMusic',
    'ClipboardCopy', 'ClipboardPaste', 'ClipboardPen', 'ClipboardType',
  ],
  'Media & Content': [
    'Image', 'Camera', 'Video', 'Film', 'Clapperboard', 'Mic', 'Music',
    'Palette', 'Brush', 'Pen', 'Pencil', 'Type', 'Bold', 'Italic',
    'FileText', 'FileImage', 'Files', 'FolderOpen', 'Folder',
    'Megaphone', 'Newspaper', 'Rss', 'Share2', 'Globe',
    'ImagePlus', 'ScanFace', 'Focus', 'Aperture', 'Podcast',
    'Radio', 'Volume2', 'PlayCircle', 'PauseCircle',
    'Youtube', 'Instagram', 'Twitter', 'Facebook', 'Twitch',
    'ImageMinus', 'ImageOff', 'ImageDown', 'ImageUp',
    'CameraOff', 'VideoOff', 'MicOff', 'VolumeX', 'Volume1', 'Volume',
    'Music2', 'Music3', 'Music4', 'Disc', 'Disc2', 'Disc3',
    'Play', 'Pause', 'SkipBack', 'SkipForward', 'Rewind', 'FastForward',
    'Repeat', 'Repeat1', 'Repeat2', 'Shuffle',
    'MonitorPlay', 'MonitorSpeaker', 'Tv', 'Tv2',
    'Headphones', 'HeadphoneOff', 'Speaker',
    'PenLine', 'PenTool', 'Highlighter', 'PaintbrushVertical',
    'Underline', 'Strikethrough', 'Subscript', 'Superscript',
    'AlignLeft', 'AlignCenter', 'AlignRight', 'AlignJustify',
    'Heading1', 'Heading2', 'Heading3', 'Heading4', 'Heading5', 'Heading6',
    'Quote', 'TextQuote', 'Text', 'TextCursor', 'TextCursorInput',
    'Pilcrow', 'WrapText', 'RemoveFormatting', 'Spellcheck',
  ],
  'Arrows & Direction': [
    'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown',
    'ArrowUpRight', 'ArrowUpLeft', 'ArrowDownRight', 'ArrowDownLeft',
    'ArrowBigRight', 'ArrowBigLeft', 'ArrowBigUp', 'ArrowBigDown',
    'ArrowRightLeft', 'ArrowUpDown', 'ArrowDownUp',
    'ArrowRightFromLine', 'ArrowLeftFromLine', 'ArrowUpFromLine', 'ArrowDownFromLine',
    'ArrowRightToLine', 'ArrowLeftToLine', 'ArrowUpToLine', 'ArrowDownToLine',
    'MoveRight', 'MoveLeft', 'MoveUp', 'MoveDown',
    'MoveHorizontal', 'MoveVertical', 'MoveDiagonal', 'MoveDiagonal2',
    'ChevronRight', 'ChevronLeft', 'ChevronUp', 'ChevronDown',
    'ChevronsRight', 'ChevronsLeft', 'ChevronsUp', 'ChevronsDown',
    'ChevronsUpDown', 'ChevronsLeftRight',
    'CornerRightDown', 'CornerRightUp', 'CornerLeftDown', 'CornerLeftUp',
    'CornerDownRight', 'CornerDownLeft', 'CornerUpRight', 'CornerUpLeft',
    'Undo', 'Undo2', 'Redo', 'Redo2',
    'IterationCw', 'IterationCcw',
  ],
  'Navigation & Layout': [
    'LayoutDashboard', 'LayoutGrid', 'LayoutList', 'Layers', 'Grid3X3',
    'Menu', 'MoreHorizontal', 'MoreVertical', 'Grip', 'GripVertical',
    'ExternalLink', 'Link', 'Link2', 'Link2Off', 'Unlink', 'Unlink2',
    'Search', 'Filter', 'SlidersHorizontal', 'SlidersVertical', 'Settings', 'Cog',
    'Eye', 'EyeOff', 'Bell', 'BellRing', 'Inbox', 'Mail', 'Send',
    'PanelLeft', 'PanelRight', 'PanelTop', 'PanelBottom',
    'PanelLeftOpen', 'PanelRightOpen', 'PanelTopOpen', 'PanelBottomOpen',
    'PanelLeftClose', 'PanelRightClose', 'PanelTopClose', 'PanelBottomClose',
    'Maximize', 'Maximize2', 'Minimize', 'Minimize2', 'Move', 'Crosshair',
    'SquareStack', 'Columns2', 'Columns3', 'Columns4', 'Rows2', 'Rows3', 'Rows4',
    'Table', 'Table2', 'TableProperties', 'Sheet',
    'LayoutTemplate', 'LayoutPanelLeft', 'LayoutPanelTop',
    'Sidebar', 'SidebarOpen', 'SidebarClose',
    'GalleryHorizontal', 'GalleryHorizontalEnd', 'GalleryVertical', 'GalleryVerticalEnd',
    'GalleryThumbnails',
    'Grid2X2', 'Grid2X2Plus',
    'AppWindow', 'AppWindowMac',
    'Fullscreen', 'ShrinkIcon', 'Expand',
    'Dock', 'Ratio', 'RectangleHorizontal', 'RectangleVertical',
    'Square', 'SquareDashed', 'SquareDashedBottom',
    'BellDot', 'BellMinus', 'BellOff', 'BellPlus',
    'SearchCheck', 'SearchCode', 'SearchSlash', 'SearchX',
    'FilterX', 'ListFilter',
    'BookmarkCheck', 'Hash', 'AtSign',
  ],
  'Files & Folders': [
    'File', 'FileText', 'FileImage', 'FileVideo', 'FileAudio', 'FileCode', 'FileCode2',
    'FileJson', 'FileJson2', 'FileSpreadsheet', 'FileBarChart', 'FileBarChart2',
    'FileClock', 'FileDiff', 'FileDigit', 'FileDown', 'FileUp',
    'FileEdit', 'FileHeart', 'FileInput', 'FileOutput',
    'FileKey', 'FileKey2', 'FileLock', 'FileLock2',
    'FileMinus', 'FileMinus2', 'FilePlus', 'FilePlus2',
    'FilePen', 'FilePenLine', 'FileQuestion',
    'FileSearch', 'FileSearch2', 'FileSignature',
    'FileScan', 'FileStack', 'FileSymlink', 'FileTerminal',
    'FileType', 'FileType2', 'FileVolume', 'FileVolume2',
    'FileWarning', 'FileX', 'FileX2',
    'FileCheck', 'FileCheck2', 'FileBadge', 'FileBadge2',
    'Files', 'FolderOpen', 'Folder', 'FolderPlus', 'FolderMinus',
    'FolderClosed', 'FolderCheck', 'FolderClock', 'FolderCog',
    'FolderDot', 'FolderDown', 'FolderEdit', 'FolderGit', 'FolderGit2',
    'FolderHeart', 'FolderInput', 'FolderKanban', 'FolderKey',
    'FolderLock', 'FolderOpenDot', 'FolderOutput', 'FolderPen',
    'FolderRoot', 'FolderSearch', 'FolderSearch2', 'FolderSymlink',
    'FolderSync', 'FolderTree', 'FolderUp', 'FolderX',
    'Archive', 'ArchiveRestore', 'ArchiveX',
    'Clipboard', 'ClipboardCheck', 'ClipboardCopy', 'ClipboardList',
    'ClipboardPaste', 'ClipboardPen', 'ClipboardType', 'ClipboardX',
    'NotepadText', 'NotepadTextDashed', 'Notebook', 'NotebookPen', 'NotebookTabs', 'NotebookText',
  ],
  'System & Tools': [
    'Cpu', 'Database', 'Server', 'HardDrive', 'Cloud', 'CloudUpload',
    'Download', 'Upload', 'RefreshCw', 'RotateCcw', 'Zap', 'Sparkles',
    'Bot', 'Brain', 'Wand2', 'Terminal', 'Code', 'GitBranch',
    'Activity', 'Signal', 'Wifi', 'Bluetooth', 'Smartphone', 'Monitor',
    'Power', 'Battery', 'Plug', 'Cable',
    'Trash2', 'Save', 'Copy', 'Scissors', 'Eraser',
    'AlertCircle', 'AlertTriangle', 'Info', 'HelpCircle', 'Ban',
    'Plus', 'Minus', 'X', 'Check',
    'ScanLine', 'Fingerprint', 'QrCode', 'Nfc',
    'Webhook', 'FileCode2',
    'CloudDownload', 'CloudOff', 'CloudRain', 'CloudSnow', 'CloudDrizzle',
    'CloudFog', 'CloudHail', 'CloudLightning', 'CloudMoon', 'CloudSun',
    'WifiOff', 'SignalHigh', 'SignalLow', 'SignalMedium', 'SignalZero',
    'BatteryCharging', 'BatteryFull', 'BatteryLow', 'BatteryMedium', 'BatteryWarning',
    'PlugZap', 'PlugZap2', 'PowerOff',
    'MonitorCheck', 'MonitorDot', 'MonitorDown', 'MonitorOff',
    'MonitorSmartphone', 'MonitorUp', 'MonitorX',
    'Laptop', 'Laptop2', 'TabletSmartphone', 'Tablet',
    'Watch', 'Gamepad', 'Gamepad2', 'Joystick',
    'Keyboard', 'Mouse', 'MousePointer', 'MousePointer2',
    'Router', 'ServerCog', 'ServerCrash', 'ServerOff',
    'DatabaseBackup', 'DatabaseZap',
    'HardDriveDownload', 'HardDriveUpload',
    'MemoryStick', 'CircuitBoard', 'Binary',
  ],
  'Finance & Business': [
    'Briefcase', 'PiggyBank', 'Vault', 'Building2',
    'FileSpreadsheet', 'FileBarChart', 'FileSignature',
    'Stamp', 'Scale', 'Gavel', 'BadgeInfo', 'CircleHelp',
    'TrendingDown', 'Percent', 'Calculator',
    'Receipt', 'CreditCard', 'Banknote', 'HandCoins',
    'BriefcaseBusiness', 'BriefcaseMedical',
    'Presentation', 'GanttChart', 'GanttChartSquare',
    'BarChart2', 'BarChart4',
    'AreaChart', 'LineChart',
    'Gem', 'BadgeDollarSign', 'BadgeCent', 'Coins',
    'CircleDollarSign', 'DollarSign', 'Euro', 'PoundSterling',
    'IndianRupee', 'JapaneseYen', 'SwissFranc', 'RussianRuble',
    'Bitcoin', 'Ethereum',
    'ArrowDownWideNarrow', 'ArrowUpWideNarrow', 'ArrowUpNarrowWide', 'ArrowDownNarrowWide',
  ],
  'Communication': [
    'MessageCircle', 'MessageSquare', 'MessagesSquare', 'AtSign',
    'Phone', 'PhoneCall', 'PhoneIncoming', 'PhoneOutgoing',
    'Mail', 'MailOpen', 'Mailbox', 'Forward', 'Reply', 'ReplyAll',
    'Headphones', 'Mic', 'MicOff',
    'Share', 'Share2', 'SendHorizontal', 'Paperclip',
    'MessageCircleCode', 'MessageCircleDashed', 'MessageCircleHeart',
    'MessageCircleMore', 'MessageCircleOff', 'MessageCirclePlus',
    'MessageCircleQuestion', 'MessageCircleReply', 'MessageCircleWarning',
    'MessageCircleX',
    'MessageSquareCode', 'MessageSquareDashed', 'MessageSquareDiff',
    'MessageSquareDot', 'MessageSquareHeart', 'MessageSquareMore',
    'MessageSquareOff', 'MessageSquarePlus', 'MessageSquareQuote',
    'MessageSquareReply', 'MessageSquareShare', 'MessageSquareText',
    'MessageSquareWarning', 'MessageSquareX',
    'PhoneForwarded', 'PhoneMissed', 'PhoneOff',
    'MailCheck', 'MailMinus', 'MailPlus', 'MailSearch', 'MailWarning', 'MailX',
    'Inbox', 'Contact', 'Contact2', 'ContactRound',
    'Globe', 'Globe2', 'Languages', 'Voicemail',
  ],
  'Health & Wellness': [
    'Heart', 'HeartPulse', 'Stethoscope', 'Pill', 'Syringe',
    'Thermometer', 'Activity', 'Dumbbell', 'Footprints',
    'Apple', 'Salad', 'Droplets', 'Sun', 'Moon', 'CloudSun',
    'Smile', 'Frown', 'Meh', 'Laugh',
    'HeartCrack', 'HeartHandshake', 'HeartOff',
    'Cross', 'CirclePlus',
    'Ambulance', 'BriefcaseMedical',
    'Bed', 'BedDouble', 'BedSingle',
    'Brain', 'Ear', 'EarOff', 'Eye', 'EyeOff',
    'Hand', 'HandMetal', 'Bone',
    'Bike', 'PersonStanding', 'Accessibility',
    'SunMedium', 'SunDim', 'Sunrise', 'Sunset', 'MoonStar',
    'SmilePlus', 'Annoyed', 'Angry',
    'Baby',
  ],
  'Security & Auth': [
    'Shield', 'ShieldCheck', 'ShieldAlert', 'ShieldBan', 'ShieldEllipsis',
    'ShieldHalf', 'ShieldMinus', 'ShieldOff', 'ShieldPlus', 'ShieldQuestion',
    'ShieldX',
    'Lock', 'LockKeyhole', 'LockOpen', 'Unlock',
    'Key', 'KeyRound', 'KeySquare',
    'Fingerprint', 'ScanFace', 'ScanEye',
    'Eye', 'EyeOff',
    'UserCheck', 'UserX', 'UserCog',
    'LogIn', 'LogOut',
    'AlertCircle', 'AlertTriangle', 'AlertOctagon',
    'Ban', 'OctagonAlert', 'OctagonX', 'OctagonPause',
    'TriangleAlert', 'CircleAlert', 'CircleX',
    'Bug', 'BugOff', 'BugPlay',
    'Siren',
    'Verified', 'BadgeCheck', 'BadgeAlert', 'BadgeX', 'BadgeMinus', 'BadgePlus',
  ],
  'Transport & Travel': [
    'Car', 'CarFront', 'CarTaxiFront', 'Bus', 'BusFront', 'TramFront',
    'Train', 'TrainFront', 'TrainTrack',
    'Truck', 'Bike', 'Ship', 'Sailboat',
    'Plane', 'PlaneLanding', 'PlaneTakeoff',
    'Rocket', 'Orbit',
    'Fuel', 'Gauge',
    'MapPin', 'MapPinned', 'MapPinOff', 'MapPinCheck', 'MapPinPlus', 'MapPinX',
    'Map', 'Globe', 'Globe2', 'Navigation', 'Navigation2', 'Compass',
    'Signpost', 'SignpostBig', 'Milestone', 'Route',
    'Luggage', 'BaggageClaim',
    'ParkingCircle', 'ParkingSquare', 'ParkingMeter',
    'TrafficCone', 'Construction',
    'Anchor',
  ],
  'Shapes & Geometry': [
    'Circle', 'CircleDot', 'CircleDashed', 'CircleFadingPlus',
    'Square', 'SquareDashed', 'SquareEqual', 'SquareSlash',
    'Triangle', 'Pentagon', 'Hexagon', 'Octagon',
    'Diamond', 'RectangleHorizontal', 'RectangleVertical',
    'Star', 'StarHalf', 'StarOff',
    'Heart', 'HeartCrack', 'HeartOff',
    'Spline', 'Tangent',
    'Shapes', 'Component',
    'Box', 'BoxSelect', 'SquareMousePointer',
    'Blend', 'Contrast', 'Droplet', 'Droplets',
    'Diameter', 'Radius',
    'Proportions', 'Ratio',
    'Infinity', 'Sigma', 'Pi', 'Omega',
    'Hash', 'Asterisk', 'Equal', 'EqualNot',
    'Divide', 'Superscript', 'Subscript',
    'Braces', 'Brackets', 'Parentheses', 'Regex',
    'CircleDotDashed', 'SquareDashedBottom', 'SquareDashedBottomCode',
  ],
  'Weather & Nature': [
    'Sun', 'SunMedium', 'SunDim', 'Sunrise', 'Sunset', 'SunMoon',
    'Moon', 'MoonStar',
    'Cloud', 'CloudSun', 'CloudMoon', 'CloudRain', 'CloudSnow',
    'CloudDrizzle', 'CloudFog', 'CloudHail', 'CloudLightning',
    'CloudOff',
    'Wind', 'Tornado', 'Rainbow', 'Umbrella', 'UmbrellaOff',
    'Snowflake', 'Haze', 'ThermometerSun', 'ThermometerSnowflake',
    'Waves', 'Droplet', 'Droplets',
    'TreeDeciduous', 'TreePine', 'Trees', 'Palmtree',
    'Flower', 'Flower2', 'Sprout', 'Clover', 'Leaf', 'LeafyGreen',
    'Shrub', 'Mountain', 'MountainSnow',
    'Bird', 'Bug', 'Cat', 'Dog', 'Fish', 'Rabbit', 'Rat',
    'Squirrel', 'Turtle', 'Snail', 'Worm',
    'Shell', 'Paw', 'Feather',
    'Flame', 'FlameKindling',
  ],
  'Science & Education': [
    'GraduationCap', 'School', 'BookOpen', 'BookCopy', 'BookMarked',
    'BookOpenCheck', 'BookOpenText', 'BookText', 'BookUp', 'BookDown',
    'BookA', 'BookAudio', 'BookCheck', 'BookDashed', 'BookHeart',
    'BookImage', 'BookKey', 'BookLock', 'BookMinus', 'BookPlus',
    'BookType', 'BookUser', 'BookX', 'Library', 'LibraryBig',
    'Atom', 'Beaker', 'FlaskConical', 'FlaskConicalOff', 'FlaskRound',
    'Microscope', 'Telescope', 'TestTube', 'TestTubes', 'TestTube2',
    'Dna', 'Orbit',
    'Brain', 'Lightbulb', 'LightbulbOff',
    'Puzzle', 'PuzzlePiece',
    'Binary', 'Braces', 'Code', 'Code2', 'CodeXml',
    'Variable', 'FileCode', 'FileCode2',
    'Presentation', 'Projector', 'WholeWord',
    'Languages', 'Spellcheck', 'CaseSensitive', 'CaseUpper', 'CaseLower',
  ],
  'Sports & Games': [
    'Trophy', 'Medal', 'Award', 'Crown',
    'Target', 'Crosshair',
    'Dumbbell', 'Bike',
    'Flag', 'FlagTriangleRight',
    'Swords', 'Sword', 'Shield',
    'Gamepad', 'Gamepad2', 'Joystick', 'Dice1', 'Dice2', 'Dice3', 'Dice4', 'Dice5', 'Dice6',
    'Puzzle', 'PuzzlePiece',
    'Music', 'Music2', 'Guitar', 'Drum', 'Piano',
    'Drama', 'Theater', 'Clapperboard',
    'Volleyball', 'Tennis', 'Dribbble',
    'Timer', 'TimerReset', 'Stopwatch',
  ],
  'Emoji & Status': [
    'Smile', 'SmilePlus', 'Frown', 'Meh', 'Laugh', 'Annoyed', 'Angry',
    'ThumbsUp', 'ThumbsDown',
    'PartyPopper', 'Sparkle', 'Sparkles',
    'Zap', 'ZapOff',
    'AlertCircle', 'AlertTriangle', 'AlertOctagon',
    'Info', 'HelpCircle', 'CircleHelp',
    'Check', 'CheckCheck', 'CheckCircle', 'CheckCircle2',
    'X', 'XCircle', 'XOctagon', 'XSquare',
    'CircleSlash', 'CircleSlash2',
    'TriangleAlert', 'OctagonAlert', 'OctagonX',
    'Verified', 'BadgeCheck', 'BadgeAlert', 'BadgeX',
    'CircleDot', 'CircleDotDashed',
    'Loader', 'Loader2', 'LoaderCircle',
    'Hourglass', 'Timer',
    'Eye', 'EyeOff', 'EyeClosed',
    'Bell', 'BellDot', 'BellOff', 'BellRing',
    'Volume', 'Volume1', 'Volume2', 'VolumeX',
  ],
  'Actions & Editing': [
    'Plus', 'PlusCircle', 'PlusSquare',
    'Minus', 'MinusCircle', 'MinusSquare',
    'Edit', 'Edit2', 'Edit3',
    'Pen', 'PenLine', 'PenTool', 'Pencil', 'PencilLine',
    'Trash', 'Trash2',
    'Copy', 'CopyCheck', 'CopyMinus', 'CopyPlus', 'CopySlash', 'CopyX',
    'Save', 'SaveAll',
    'Download', 'DownloadCloud',
    'Upload', 'UploadCloud',
    'RefreshCw', 'RefreshCcw', 'RotateCw', 'RotateCcw',
    'Undo', 'Undo2', 'Redo', 'Redo2',
    'Scissors',
    'Eraser',
    'Maximize', 'Maximize2', 'Minimize', 'Minimize2',
    'Move', 'MoveHorizontal', 'MoveVertical',
    'GripHorizontal', 'GripVertical', 'Grip',
    'Merge', 'Split', 'GitMerge', 'GitFork',
    'Replace', 'ReplaceAll',
    'WandSparkles', 'Wand', 'Wand2',
    'Crop', 'Slice', 'SwatchBook',
    'Flip', 'FlipHorizontal', 'FlipHorizontal2', 'FlipVertical', 'FlipVertical2',
    'ScanSearch',
  ],
  'Development & Code': [
    'Code', 'Code2', 'CodeXml', 'Braces', 'Brackets',
    'Terminal', 'TerminalSquare',
    'GitBranch', 'GitCommit', 'GitCompare', 'GitFork', 'GitGraph', 'GitMerge',
    'GitPullRequest', 'GitPullRequestArrow', 'GitPullRequestClosed', 'GitPullRequestCreate', 'GitPullRequestDraft',
    'Bug', 'BugOff', 'BugPlay',
    'Database', 'DatabaseBackup', 'DatabaseZap',
    'Server', 'ServerCog', 'ServerCrash', 'ServerOff',
    'Container', 'Layers', 'Layers2', 'Layers3',
    'Workflow', 'Webhook',
    'Globe', 'Globe2', 'GlobeLock',
    'Cloud', 'CloudCog',
    'Variable', 'Binary', 'Regex',
    'Component', 'Blocks',
    'Network', 'Waypoints',
    'Cpu', 'CircuitBoard', 'MemoryStick',
    'FileJson', 'FileJson2', 'FileCode', 'FileCode2', 'FileTerminal',
    'SquareCode', 'SquareTerminal',
    'Laptop', 'Laptop2', 'Monitor', 'MonitorSmartphone',
  ],
};

// Flat list: curated first, then all remaining Lucide icons
const curatedSet = new Set(Object.values(CURATED_ICONS).flat());
const allLucideIcons = Object.keys(LucideIcons).filter(
  (key) =>
    key[0] === key[0].toUpperCase() &&
    !key.endsWith('Icon') &&
    key !== 'default' &&
    key !== 'createLucideIcon' &&
    isIcon((LucideIcons as any)[key])
);
const remainingIcons = allLucideIcons.filter((k) => !curatedSet.has(k));

export const AVAILABLE_ICONS: string[] = [
  ...Array.from(curatedSet).filter((k) => isIcon((LucideIcons as any)[k])),
  ...remainingIcons,
];
